import { useMutation, useQueryClient } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import { scheduleFeedReconciliation } from "@bw/shared/hooks";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The community membership mutations the detail page exposes: join + leave. Each
 * posts to the communities endpoint and, on success, invalidates the `social`
 * arm (prefix — refetches the hub list so its My / Discover split + Member badge
 * update) AND the specific `social.community(id)` slot (refetches the open detail
 * page so its CTA flips and its member_count reflects the SERVER's truth).
 *
 * No optimistic update: the cache is NOT mutated before the server confirms — no
 * client-side member_count +1, no phantom CTA flip. A rejected join (403 /
 * 409 already-member / network) leaves the membership exactly as the server last
 * reported it and surfaces on `mutation.error` for the page to show honestly.
 *
 * Re-entrancy is the page's concern: it disables the button while `isPending` so
 * a double-click fires the mutation once.
 */
export function useCommunityMembership(
  communityId: string,
  // "inline": the caller shows guardian denials in its own error area (#2691).
  options: { guardianPrompt?: "inline" } = {},
) {
  const queryClient = useQueryClient();
  const meta = options.guardianPrompt ? { guardianPrompt: options.guardianPrompt } : undefined;

  const invalidate = () => {
    // Prefix invalidation of the whole social arm — the hub list refetches.
    void queryClient.invalidateQueries({ queryKey: queryKeys.social.all });
    // And the specific detail slot — the open detail page refetches.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.social.community(communityId),
    });
    scheduleFeedReconciliation(queryClient, [
      { scope: "home" },
      { scope: "communities" },
      { scope: "community", subject: communityId },
    ]);
  };

  const join = useMutation<void, unknown, void>({
    meta,
    mutationFn: () => communityApi.join(communityId),
    onSuccess: () => invalidate(),
  });

  const leave = useMutation<void, unknown, void>({
    meta,
    mutationFn: () => communityApi.leave(communityId),
    onSuccess: () => invalidate(),
  });

  const request = useMutation({
    meta,
    mutationFn: () => communityApi.createJoinRequest(communityId),
    onSuccess: () => invalidate(),
  });

  return { join, leave, request };
}
